import { FormHelperText, TextFieldProps } from '@mui/material';
import React, {
  FC,
  FocusEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';

import translate from '../../../../i18n/translate';
import MuiIcon from '../../../MUI/MuiIcon';

import * as Styled from './styled';

type Props = TextFieldProps & {
  isOpen: boolean;
  shouldValidate?: boolean;
  value: HTMLInputElement['value'];
};

/**
 * Trigger for the topic selection in compose modal
 *
 * @param props                Props passed to the component
 * @param props.isOpen         Whether the topic selection is open
 * @param props.shouldValidate Whether the input should be validated
 * @param props.value          The value of the input
 *
 * @returns                    Component itself
 */
const ComposeTopicTrigger: FC<Props> = ({
  isOpen,
  shouldValidate = true,
  value,
  ...restProps
}) => {
  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setIsValid(inputRef.current?.checkValidity() ?? false);
  }, [value]);

  /**
   * Handle blur event on the input
   *
   * @param event The event that took place
   */
  const handleBlur: FocusEventHandler<HTMLInputElement> = event => {
    event.preventDefault();

    if (isTouched) {
      return;
    }

    setIsTouched(true);
  };

  const shouldShowError = !isValid && shouldValidate;

  return (
    <>
      <Styled.Input
        error={shouldShowError}
        fullWidth={true}
        inputRef={inputRef}
        label={translate('TOPIC')}
        onBlur={handleBlur}
        placeholder={translate('COMPOSE__PLACEHOLDER__TOPIC')}
        slotProps={{
          htmlInput: { readOnly: false, required: true },
          input: {
            autoComplete: 'off',
            endAdornment: (
              <MuiIcon icon={isOpen ? 'arrowDropUp' : 'arrowDropDown'} />
            ),
          },
          inputLabel: {
            // This places input label in input border
            shrink: true,
          },
        }}
        value={value}
        {...restProps}
      />
      {shouldShowError && (
        <FormHelperText error={true} variant="filled">
          {translate('GENERAL__FILL_FIELD')}
        </FormHelperText>
      )}
    </>
  );
};

export default ComposeTopicTrigger;
